import {
  withSharedAssetUrl,
  withSharedAssetUrlCSS,
  withPublicAssetUrlCSS,
  withPublicAssetUrl,
} from '../../lib/utils/cdn'

export type FontFamily = 'Inter' | 'Matter' | 'MatterforGrammarly' | 'Glyph' | 'MessinaSerifWeb'

type FontInfo = {
  woff2: string
}
type FontWeightInfo = {
  readonly [key: number]: FontInfo
}

export const fontsInfo: Record<FontFamily, FontWeightInfo> = {
  Inter: {
    400: {
      woff2: 'fonts/inter-regular.woff2',
    },
    500: {
      woff2: 'fonts/inter-medium.woff2',
    },
    600: {
      woff2: 'fonts/inter-semi-bold.woff2',
    },
    700: {
      woff2: 'fonts/inter-bold.woff2',
    },
  },
  Matter: {
    670: {
      woff2: 'fonts/matter-semi-bold.woff2',
    },
    570: {
      woff2: 'fonts/matter-medium.woff2',
    },
  },
  MatterforGrammarly: {
    400: {
      woff2: 'fonts/MatterforGrammarlyVF.woff2',
    },
  },
  Glyph: {
    400: {
      woff2: 'fonts/glyph-regular.woff2',
    },
    700: {
      woff2: 'fonts/glyph-bold.woff2',
    },
  },
  MessinaSerifWeb: {
    400: {
      woff2: 'fonts/MessinaSerifWeb-VF-Upright.woff2',
    },
  },
}

export const getFontsToPreload = (fontFamilies: FontFamily[]) => {
  const fontsToPreload: Array<{ key: string; url: string; format: string }> = []

  fontFamilies.forEach((family) => {
    const fontWeight = fontsInfo[family]

    for (const [weight, fontFormat] of Object.entries(fontWeight)) {
      for (const [format, url] of Object.entries(fontFormat)) {
        fontsToPreload.push({
          key: `${family}-${weight}-${format}`,
          url:
            family === 'MessinaSerifWeb' || family === 'MatterforGrammarly'
              ? withPublicAssetUrl(url)
              : withSharedAssetUrl(url), // @todo we probably need to upload MessinaSerifWeb and MatterforGrammarly to CDN and then use withSharedAssetUrl only.
          format,
        })
      }
    }
  })

  return fontsToPreload
}

const fontFaceStyleMap: { [f in FontFamily]: string } = {
  Inter: `
@font-face {
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url(${withSharedAssetUrlCSS(fontsInfo.Inter[400].woff2)}) format('woff2');
}

@font-face {
  font-family: Inter;
  font-style: normal;
  font-weight: 500;
  font-display: swap;
  src: url(${withSharedAssetUrlCSS(fontsInfo.Inter[500].woff2)}) format('woff2');
}

@font-face {
  font-family: Inter;
  font-style: normal;
  font-weight: 600;
  font-display: swap;
  src: url(${withSharedAssetUrlCSS(fontsInfo.Inter[600].woff2)}) format('woff2');
}

@font-face {
  font-family: Inter;
  font-style: normal;
  font-weight: 700;
  font-display: swap;
  src: url(${withSharedAssetUrlCSS(fontsInfo.Inter[700].woff2)}) format('woff2');
}
`,
  Matter: `
@font-face {
  font-family: Matter;
  font-style: normal;
  font-weight: 670;
  font-display: swap;
  src: url(${withSharedAssetUrlCSS(fontsInfo.Matter[670].woff2)}) format('woff2');
}
@font-face {
  font-family: Matter;
  font-style: normal;
  font-weight: 570;
  font-display: swap;
  src: url(${withSharedAssetUrlCSS(fontsInfo.Matter[570].woff2)}) format('woff2');
}
`,
  Glyph: `
@font-face {
  font-family: Glyph;
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url(${withSharedAssetUrlCSS(fontsInfo.Glyph[400].woff2)}) format('woff2');
}

@font-face {
  font-family: Glyph;
  font-weight: 700;
  font-display: swap;
  src: url(${withSharedAssetUrlCSS(fontsInfo.Glyph[700].woff2)}) format('woff2');
}
`,
  MatterforGrammarly: `
@font-face {
  font-family: 'Matter for Grammarly';
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url(${withPublicAssetUrlCSS(fontsInfo.MatterforGrammarly[400].woff2)}) format('woff2');
}
`,
  MessinaSerifWeb: `
@font-face {
  font-family: 'MessinaSerifWeb';
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url(${withPublicAssetUrlCSS(fontsInfo.MessinaSerifWeb[400].woff2)}) format('woff2');
}
`,
}
// @todo change this to withSharedAssetUrlCSS after adding font to CDN.

export const getStyleFontFaces = (fontFamilies: FontFamily[]): string => {
  return fontFamilies.reduce((acc, curr) => {
    const fontFamilyStyle = fontFaceStyleMap[curr]
    return acc.concat(fontFamilyStyle)
  }, '')
}
